import { useCallback, useMemo, useState } from 'react';
import {
  committedQtyConfigForUnit,
  planUnit,
  type SubscriptionAudience,
  type SubscriptionInterval,
} from '@hero/core';
import type { BillingUnit, Business, Currency } from '@hero/schema';

export interface UsePlanPickerManagerOptions {
  initialPlanCode?: string;
  initialInterval?: SubscriptionInterval;
  initialCommittedVehicleQty?: number;
  initialCommittedOrderQty?: number;
  initialCurrency?: Currency;
  /**
   * Unit to report while no plan is selected. Only matters on the
   * empty picker — once a plan is chosen its own unit wins.
   */
  initialUnit?: BillingUnit;
  /**
   * Which ladder sizes the credit purchase. Only read on `order`,
   * where the floor is the ladder's own first rung and differs between
   * businesses and between a company and an owner-operator.
   */
  business?: Business;
  audience?: SubscriptionAudience;
}

/**
 * Container for plan-picker state (plan code, interval, committed
 * quantity, currency). Consumed by `useBillingCheckoutManager` and any
 * other plan-picker surface that wants the same field shape.
 *
 * The two committed quantities are held SEPARATELY and both survive a
 * plan switch. A user comparing a seat plan against a volume plan flips
 * back and forth; collapsing them into one number would either reset
 * their choice on every switch or carry "50" from orders over to seats,
 * where it means something completely different.
 *
 * `committedQty` / `setCommittedQty` are the unit-resolved view — what a
 * single stepper binds to.
 */
export function usePlanPickerManager(options: UsePlanPickerManagerOptions = {}) {
  const [planCode, setPlanCode] = useState<string | undefined>(options.initialPlanCode);
  const [interval, setInterval] = useState<SubscriptionInterval>(
    options.initialInterval ?? 'month',
  );
  const [committedVehicleQty, setCommittedVehicleQty] = useState<number>(
    options.initialCommittedVehicleQty ?? 1,
  );
  const [committedOrderQty, setCommittedOrderQty] = useState<number>(
    options.initialCommittedOrderQty ?? committedQtyConfigForUnit('order').default,
  );
  const [currency, setCurrency] = useState<Currency>(options.initialCurrency ?? 'SAR');

  const unit: BillingUnit = planCode
    ? planUnit(planCode)
    : options.initialUnit ?? 'vehicle';
  const isOrderUnit = unit === 'order';

  const qtyConfig = useMemo(
    () => committedQtyConfigForUnit(unit, options.business, options.audience),
    [unit, options.business, options.audience],
  );

  const committedQty = isOrderUnit ? committedOrderQty : committedVehicleQty;
  const setCommittedQty = useCallback(
    (qty: number) => {
      if (isOrderUnit) setCommittedOrderQty(qty);
      else setCommittedVehicleQty(qty);
    },
    [isOrderUnit],
  );

  return {
    planCode,
    setPlanCode,
    interval,
    setInterval,
    committedVehicleQty,
    setCommittedVehicleQty,
    committedOrderQty,
    setCommittedOrderQty,
    /** What the selected plan meters. */
    unit,
    /** The committed quantity for the selected plan's unit. */
    committedQty,
    setCommittedQty,
    /** `min` / `default` / `snapPoints` for the selected plan's unit. */
    qtyConfig,
    currency,
    setCurrency,
    selection: {
      planCode,
      interval,
      committedVehicleQty,
      committedOrderQty,
      currency,
    },
    // The credit ladder has a real floor — the smallest bundle sold —
    // and the free-form box above the slider can go under it. Checking
    // the unit's own `min` rather than `> 0` is what stops the screen
    // quoting a purchase the backend will refuse to price.
    isValid: !!planCode && committedQty >= qtyConfig.min,
  };
}
